import React, { useEffect, useState } from 'react';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';

/**
 * OAuth Relay Page
 * 
 * This page receives the redirect from Rails after OAuth completion.
 * It extracts query params and posts them to the opener window, then closes itself.
 * 
 * Rails should redirect to: /integrations/oauth-callback?status=success&code=google_calendar&post_connect_next_url=...
 */
const OAuthRelayPage = () => {
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Completing authentication...');
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    try {
      // Parse query parameters from URL
      const params = new URLSearchParams(window.location.search);
      const status = params.get('status');
      const code = params.get('code');
      const postConnectNextUrl = params.get('post_connect_next_url');
      const errorMessage = params.get('message') || params.get('error');

      // Validate that we have the minimum required params
      if (!status || !code) {
        throw new Error('Missing required parameters');
      }

      // Update UI based on status
      if (status === 'success') {
        setStatus('success');
        setMessage('Authentication successful!');
      } else {
        setStatus('error');
        setMessage(errorMessage || 'Authentication failed');
      }

      // Post message to opener window (the main app)
      if (window.opener && !window.opener.closed) {
        const messageData = {
          provider: 'google', // This is checked in waitForBackendResult()
          status: status,
          code: code,
          post_connect_next_url: postConnectNextUrl,
          message: errorMessage,
        };

        // Post to same origin only (security)
        window.opener.postMessage(messageData, window.location.origin);

        // Only auto-close on success with countdown
        if (status === 'success') {
          // Start countdown
          let timeLeft = 3;
          const countdownInterval = setInterval(() => {
            timeLeft -= 1;
            setCountdown(timeLeft);
            
            if (timeLeft <= 0) {
              clearInterval(countdownInterval);
              window.close();
            }
          }, 1000);

          // Cleanup interval on unmount
          return () => clearInterval(countdownInterval);
        }
      } else {
        // No opener found - user might have closed the main window
        setStatus('error');
        setMessage('Unable to communicate with main window. Please close this window manually.');
      }
    } catch (err) {
      console.error('OAuth relay error:', err);
      setStatus('error');
      setMessage(err.message || 'An error occurred during authentication');
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
        <div className="text-center">
          {/* Icon based on status */}
          {status === 'processing' && (
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <LoadingOutlined className="text-blue-600 text-3xl" />
            </div>
          )}
          
          {status === 'success' && (
            <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircleOutlined className="text-emerald-600 text-3xl" />
            </div>
          )}
          
          {status === 'error' && (
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <CloseCircleOutlined className="text-red-600 text-3xl" />
            </div>
          )}

          {/* Status message */}
          <h2 className={`text-xl font-semibold mb-2 ${
            status === 'success' ? 'text-emerald-900' : 
            status === 'error' ? 'text-red-900' : 
            'text-gray-900'
          }`}>
            {status === 'success' ? 'Authentication Successful!' : 
             status === 'error' ? 'Authentication Failed' : 
             'Processing...'}
          </h2>
          
          <p className="text-gray-600 text-sm">
            {message}
          </p>

          {/* Countdown for success */}
          {status === 'success' && (
            <p className="text-gray-500 text-xs mt-2">
              Closing in {countdown} second{countdown !== 1 ? 's' : ''}...
            </p>
          )}

          {/* Manual close button for error cases */}
          {status === 'error' && (
            <button
              onClick={() => window.close()}
              className="mt-6 px-6 py-2.5 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 transition-colors"
            >
              Close Window
            </button>
          )}
        </div>

        {/* Debug info (only in development) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <details className="text-xs text-gray-500">
              <summary className="cursor-pointer font-medium mb-2">Debug Info</summary>
              <pre className="bg-gray-50 p-2 rounded overflow-auto">
                {JSON.stringify({
                  url: window.location.href,
                  params: Object.fromEntries(new URLSearchParams(window.location.search)),
                  hasOpener: !!window.opener,
                  openerClosed: window.opener?.closed,
                }, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};

export default OAuthRelayPage;